import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useOrderStore } from '../../store/order-store';
import { ProfileBadge } from '../profile-badge';
import { MoreButton } from '../more-button';
import { ServerSideDatatable } from '../serverside-datatable';
import StringUtils, { truncateString } from '../../utils';
import { OrderFilterer } from './order-filterer';
import OrderTag from '../OrderTag';

interface Props {}

export const OrderList: React.FC<Props> = () => {
  const { currentPage, loadingOrders, orders, listOrders, totalCount } =
    useOrderStore();

  const navigate = useNavigate();

  const navigateToOrder = useCallback(
    (id: number) => {
      navigate(`/orders/${id}`);
    },
    [navigate],
  );

  return (
    <ServerSideDatatable
      defaultOrderReference="orderDate"
      searchBarPlaceholder="Search by order number, customer name, email"
      columns={[
        {
          id: 'name',
          name: <h5 className="text-gray font-medium text-base">Order No.</h5>,
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <Link
              className="text-sm font-medium text-blue hover:underline"
              to={`/orders/${row.orderId}`}>
              {row.name}
            </Link>
          ),
          grow: 1.5,
          sortable: true,
          sortField: 'name',
        },
        {
          id: 'orderType',
          name: <h5 className="text-gray font-medium text-base">Order Type</h5>,
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <div className="flex flex-col font-medium text-sm">
              {StringUtils.prettify(row.orderType)}
              {row.orderType === 'retail' &&
                row.kit?.retailer &&
                ` - ${row.kit?.retailer}`}
            </div>
          ),
        },
        {
          id: 'customer',
          name: <h5 className="text-gray font-medium text-base">Customer</h5>,
          selector: data => data.user.email,
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <div className="flex flex-row hover:underline">
              <div className="flex flex-col justify-center">
                <ProfileBadge
                  firstName={row.user.firstName}
                  lastName={row.user.lastName}
                />
              </div>
              <div className="flex flex-col justify-center px-4 py-[5px]">
                <h6 className="font-semibold text-black text-sm leading-[19.6px]">
                  {row.user.firstName} {row.user.lastName}
                </h6>
                <p className="text-gray-dark text-xs leading-[18px]">
                  {row.user.email}
                </p>
              </div>
            </div>
          ),
          grow: 5,
          sortable: true,
          sortField: 'user.firstName',
        },
        {
          id: 'registeredKit',
          name: (
            <h5 className="text-gray font-medium text-base">Registered Kit</h5>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <div className="flex flex-col font-medium text-sm">
              {row.kit?.uniqueId ?? ''}
            </div>
          ),
          grow: 2,
          sortField: 'registeredKit',
        },
        {
          id: 'status',
          name: <h5 className="text-gray font-medium text-base">Status</h5>,
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => <OrderTag order={row} />,
          grow: 2,
          sortable: true,
          sortField: 'status',
        },
        {
          id: 'description',
          name: (
            <h5 className="text-gray font-medium text-base">Description</h5>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <span>
              {truncateString(
                Object.values(row?.testKit?.additionalInfo || {}).join(' '),
                20,
              )}
            </span>
          ),
          grow: 4,
        },
        {
          id: 'orderDate',
          name: (
            <h5 className="text-gray font-medium text-base">Date Created</h5>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <div className="flex flex-col font-medium text-sm">
              <div>
                {DateTime.fromISO(row.orderDate).toFormat('LL-dd-yyyy')}
              </div>
              <div>{DateTime.fromISO(row.orderDate).toFormat('hh:mm a')}</div>
            </div>
          ),
          grow: 1.75,
          sortable: true,
          sortField: 'orderDate',
        },
        {
          id: 'actions',
          name: '',
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <MoreButton>
              <button
                type="button"
                className="text-base hover:bg-blue-lighter hover:text-blue text-black font-medium w-full px-4 py-1 text-left"
                onClick={() => navigateToOrder(row.orderId)}>
                View Order
              </button>
            </MoreButton>
          ),
          grow: 1,
        },
      ]}
      data={orders}
      currentPage={currentPage}
      paginationTotalRows={totalCount}
      progressPending={loadingOrders}
      serverAction={listOrders}
      filterComponent={<OrderFilterer />}
    />
  );
};
